/**
 * Ship and defence catalogue.
 *
 * Base stats (Hülle / Schild / Schaden / Fracht) are copied verbatim from the
 * GDD §1 tables. Costs are design fill-ins scaled so that a unit's combined
 * titan+silicate mass roughly tracks its hull — which keeps the build-time
 * formula and the 30 % debris rule coherent across the fleet.
 *
 * Prices sit ~10× the original draft because the reworked economy (formulas.ts)
 * produces an order of magnitude more per hour. The target is fleets in the
 * hundreds-to-low-thousands rather than the hundreds of thousands: that keeps
 * the round-based battle simulator, which expands one object per hull, both
 * fast and readable.
 */

import type { BuildingKey, DefenseKey, ResearchKey, ResourceAmounts, ShipKey, UnitKey } from '../types.js';

export interface UnitDef {
  key: UnitKey;
  label: string;
  role: string;
  kind: 'ship' | 'defense';
  hull: number;
  shield: number;
  damage: number;
  /** Relative flight speed; defenses use 0 because they never travel. */
  speed: number;
  /** Ships only. */
  cargo?: number;
  cost: ResourceAmounts;
  requires?: Partial<{
    buildings: Partial<Record<BuildingKey, number>>;
    research: Partial<Record<ResearchKey, number>>;
  }>;
  /** Not buildable through the shipyard (event/blueprint ships). */
  eventOnly?: boolean;
  /** At most this many may exist (shield dome: 1). */
  maxCount?: number;
  sprite: string;
}

export const SHIPS: Record<ShipKey, UnitDef> = {
  probe: {
    key: 'probe',
    label: 'Aero-Sonde',
    role: 'Spionage und Sensorkartierung.',
    kind: 'ship',
    hull: 100, shield: 1, damage: 1, cargo: 5, speed: 1_800,
    cost: { silicate: 12_000 },
    requires: { buildings: { shipyard: 1 } },
    sprite: 'ship-probe',
  },
  minerShip: {
    key: 'minerShip',
    label: 'Erz-Sammelschiff',
    role: 'Abbau von Asteroiden und Trümmerfeldern.',
    kind: 'ship',
    hull: 4_000, shield: 100, damage: 5, cargo: 20_000, speed: 240,
    cost: { titan: 120_000, silicate: 70_000 },
    requires: { buildings: { shipyard: 1 } },
    sprite: 'ship-miner',
  },
  transporter: {
    key: 'transporter',
    label: 'Stellar-Transporter',
    role: 'Warentransport zwischen Planeten und Spielern.',
    kind: 'ship',
    hull: 1_200, shield: 20, damage: 5, cargo: 10_000, speed: 280,
    cost: { titan: 70_000, silicate: 70_000 },
    requires: { buildings: { shipyard: 1 } },
    sprite: 'ship-transporter',
  },
  corvette: {
    key: 'corvette',
    label: 'Vanguard-Korvette',
    role: 'Leichte Abfangeinheit für das Early-Game.',
    kind: 'ship',
    hull: 1_000, shield: 50, damage: 100, cargo: 500, speed: 320,
    cost: { titan: 36_000, silicate: 12_000 },
    requires: { buildings: { shipyard: 1 } },
    sprite: 'ship-corvette',
  },
  cruiser: {
    key: 'cruiser',
    label: 'Aegis-Kreuzer',
    role: 'Mittlerer Allrounder gegen Fregatten und Abwehr.',
    kind: 'ship',
    hull: 2_700, shield: 200, damage: 400, cargo: 800, speed: 260,
    cost: { titan: 240_000, silicate: 84_000, isotope: 24_000 },
    requires: { buildings: { shipyard: 3 }, research: { impulseDrive: 2 } },
    sprite: 'ship-cruiser',
  },
  battleship: {
    key: 'battleship',
    label: 'Nova-Schlachtschiff',
    role: 'Schwere Angriffseinheit für Großschlachten.',
    kind: 'ship',
    hull: 6_000, shield: 1_000, damage: 1_000, cargo: 1_500, speed: 190,
    cost: { titan: 540_000, silicate: 180_000, isotope: 96_000 },
    requires: { buildings: { shipyard: 5 }, research: { warpFold: 1 } },
    sprite: 'ship-battleship',
  },
  voidPhantom: {
    key: 'voidPhantom',
    label: 'Void-Phantom',
    role: 'Event-Schiff: Infiltration & Überraschungsangriff.',
    kind: 'ship',
    hull: 3_500, shield: 800, damage: 750, cargo: 600, speed: 230,
    cost: { titan: 360_000, silicate: 240_000, isotope: 120_000 },
    eventOnly: true,
    sprite: 'ship-void-phantom',
  },
  moonBreaker: {
    key: 'moonBreaker',
    label: 'Mond-Gravitron-Brecher',
    role: 'Endgame: Zerstörung feindlicher Trümmer-Monde.',
    kind: 'ship',
    hull: 100_000, shield: 50_000, damage: 5_000, cargo: 100_000, speed: 120,
    cost: { titan: 60_000_000, silicate: 48_000_000, isotope: 12_000_000, aether: 2_000 },
    requires: { buildings: { shipyard: 10 }, research: { gravitonPhysics: 1 } },
    sprite: 'ship-moon-breaker',
  },
  // Non-combat by design — excluded from espionage.ts's COMBAT_CAPABLE.
  // Gated on Warp-Fold (thematically: folds space to reach a new system) and
  // consumed on use by actions.ts's foundColony (galaxy plan Meilenstein E).
  // Cost sits well above battleship: founding a colony is a bigger
  // commitment than a single hull, design fill-in.
  colonyShip: {
    key: 'colonyShip',
    label: 'Kolonieschiff',
    role: 'Gründet eine neue Kolonie in einem fremden System (ab Ring 2).',
    kind: 'ship',
    hull: 2_000, shield: 200, damage: 0, cargo: 0, speed: 150,
    cost: { titan: 900_000, silicate: 900_000, isotope: 300_000 },
    requires: { buildings: { shipyard: 6 }, research: { warpFold: 2 } },
    sprite: 'ship-colony',
  },
};

export const DEFENSE: Record<DefenseKey, UnitDef> = {
  gauss: {
    key: 'gauss',
    label: 'Gauß-Geschütz',
    role: 'Kinetische Ballistik gegen leichte Flotten.',
    kind: 'defense',
    hull: 2_000, shield: 200, damage: 150, speed: 0,
    cost: { titan: 24_000, silicate: 7_200 },
    sprite: 'defense-gauss',
  },
  plasma: {
    key: 'plasma',
    label: 'Plasma-Batterie',
    role: 'Schwere Energie-Panzerabwehr gegen Schlachtschiffe.',
    kind: 'defense',
    hull: 10_000, shield: 2_000, damage: 3_000, speed: 0,
    cost: { titan: 600_000, silicate: 600_000 },
    requires: { research: { shieldHarmonics: 3 } },
    sprite: 'defense-plasma',
  },
  shieldDome: {
    key: 'shieldDome',
    label: 'Schildkuppel-Generator',
    role: 'Flächenschutz für Planetenstrukturen — wehrt einen Kometen-Einschlag ab.',
    kind: 'defense',
    hull: 20_000, shield: 10_000, damage: 0, speed: 0,
    cost: { titan: 120_000, silicate: 120_000 },
    requires: { research: { shieldHarmonics: 1 } },
    sprite: 'defense-shield-dome',
  },
  interceptor: {
    key: 'interceptor',
    label: 'Abfang-Raketensystem',
    role: 'Kometen- und Raketenabwehr. Eine Rakete wird pro Abfangvorgang verbraucht.',
    kind: 'defense',
    hull: 3_000, shield: 0, damage: 10_000, speed: 0,
    cost: { titan: 96_000, silicate: 24_000, isotope: 12_000 },
    requires: { buildings: { missileSilo: 1 } },
    sprite: 'defense-interceptor',
  },
};

export const UNITS: Record<UnitKey, UnitDef> = { ...SHIPS, ...DEFENSE };
export const SHIP_KEYS = Object.keys(SHIPS) as ShipKey[];
export const DEFENSE_KEYS = Object.keys(DEFENSE) as DefenseKey[];
